/**
 * Walking in a scene (lifted from the lantern-water prototype's `main.js`
 * `animate`, `move` and its residents' loop): the traveler's continuous
 * movement, its routes, the residents' patrols, and the triggers. Game axes
 * (x east, y north, `h` the ground's height), metres and seconds. The state is
 * plain data, changed in place by `step`; a client draws it (`@ab/renderer`'s
 * scene view reads it every frame).
 *
 * The traveler, each step:
 * - **Keys or pad**: the input is a screen direction (x right, y up, each
 *   component in -1..1, as many keys as are held); it is turned by
 *   `walk.inputTurn` onto the ground (the camera looks a little west of
 *   north) and cancels any route. A diagonal of two keys is normalised.
 * - **A route** (a tap, `routeTo`): the traveler heads for the next route
 *   point at full speed, dropping each point within `walk.arrive`; a step
 *   never overshoots the point it heads for. A step that cannot move at all
 *   ends the route.
 * - **Speed**: `walk.walkSpeed`, or `walk.runSpeed` while `run` is held (keys
 *   and routes alike).
 * - **Sliding**: the step moves along x, then along y, each only if the
 *   ground there is walkable (`map.canWalk`), so a diagonal into a wall
 *   slides along it; the facing follows the movement actually made.
 * - The height is the ground's under the new position (stairs, bridges).
 *
 * Residents with a patrol walk it point to point at `walk.residentSpeed`,
 * routing to each over the grid, pausing while the traveler is within
 * `walk.residentPause`, waiting `wait` seconds at each point and
 * `walk.blockedWait` when a step is blocked. The others stand.
 *
 * The state carries a `revision`, bumped by a step that changed what a client
 * draws; `pending` says when the model next needs a step by itself (a client
 * may stop stepping meanwhile, and `rest` lets that time pass).
 *
 * Triggers: a portal fires when the traveler enters its radius, but only once
 * the traveler has been outside every portal since arriving (a spawn on a
 * doorstep never sends the traveler straight back). A dungeon is not a
 * portal (ADR 0032): it is flown to from a `board` interactable, by the
 * action only (the shell's).
 *
 * Holding (slice 6b): while a dialogue or a menu is open the client sets
 * `held`; the traveler stands (input and route ignored) and every patroller
 * waits where it is, as the prototype's residents did while `dialogue` was
 * open. Things done once per visit (a wish at the fountain) are `spent`: a
 * new visit is a new state.
 */
import type { Item, Scene, Town } from '@ab/content-schema';
import { type Facing, facingFor } from './facing.ts';
import { type Point, type WalkMap, createWalkMap } from './map.ts';
import { findPath } from './path.ts';

export type Walker = {
  x: number;
  y: number;
  h: number;
  facing: Facing;
  /** Moved in the last step, and ran while doing so. */
  moving: boolean;
  running: boolean;
  /** Metres walked since the scene was entered. */
  distance: number;
  /** The route being walked: the points still ahead, the last one the destination. */
  route: Point[];
};

export type Resident = Walker & {
  readonly human: string;
  /** On the scene now (the airship's crew come and go with the time of day: `applyCrew`); always true for a town's. */
  present: boolean;
  /** A crew member's post and the work done there (null for a town's resident, or a crew member off deck). */
  post: string | null;
  action: string | null;
  readonly patrol: readonly Point[] | null;
  patrolIndex: number;
  /** Seconds to wait before walking on. */
  wait: number;
  readonly pause: number;
};

export type SceneState = {
  readonly scene: Scene;
  readonly map: WalkMap;
  readonly player: Walker;
  readonly residents: Resident[];
  /** Seconds since the scene was entered. */
  time: number;
  /** False while the traveler stands in a portal it arrived in (or since arriving, before leaving every portal). */
  portalArmed: boolean;
  /** Bumped whenever a walker's position, facing, moving or running changed (a client redraws on a new revision, never on identity). */
  revision: number;
  /** A dialogue or menu is open: nobody walks (slice 6b). */
  held: boolean;
  /** Interactables used up for this visit (a wish); `interactableNear` passes them over. */
  readonly spent: string[];
};

/** One step's input: a screen direction (x right, y up) or none, and whether run is held. */
export type StepInput = { readonly move: Point | null; readonly run: boolean };

export type Portal = Scene['portals'][number];
/** A town record (its `market` feeds a shop: `marketOffers`). */
export type { Town } from '@ab/content-schema';
export type Interactable = Scene['interactables'][number];

/** What a step did: the portal the traveler entered (null for none). */
export type StepResult = { readonly portal: Portal | null };

const walker = (x: number, y: number, facing: Facing, map: WalkMap): Walker => ({ x, y, h: map.heightAt(x, y), facing, moving: false, running: false, distance: 0, route: [] });


/** The walking model of a scene with the traveler at `spawn` (the scene's first spawn when absent or unknown). */
export const createSceneState = (scene: Scene, spawn?: string, map: WalkMap = createWalkMap(scene)): SceneState => {
  const at = scene.spawns.find((s) => s.id === spawn) ?? scene.spawns[0];
  if (at === undefined) throw new Error(`${scene.id} has no spawn`);
  const player = walker(at.x, at.y, at.facing, map);
  const residents = scene.residents.map((r): Resident => ({
    ...walker(r.x, r.y, r.facing, map),
    human: r.human,
    present: true,
    post: null,
    action: null,
    patrol: r.patrol ?? null,
    patrolIndex: 0,
    wait: r.patrol === undefined ? 0 : (r.wait ?? 0),
    pause: r.wait ?? 0,
  }));
  // The airship's crew (ADR 0033): residents that stand at posts, off deck until `applyCrew` places them.
  for (const m of scene.crew?.members ?? []) {
    residents.push({ ...walker(at.x, at.y, 's', map), human: m.human, present: false, post: null, action: null, patrol: null, patrolIndex: 0, wait: 0, pause: 0 });
  }
  return { scene, map, player, residents, time: 0, portalArmed: portalOf(scene, player) === null, revision: 0, held: false, spent: [] };
};

/** The portal whose radius a point stands in; a `board` portal never fires by walking (ADR 0033: the action boards). */
const portalOf = (scene: Scene, p: Point): Portal | null => scene.portals.find((q) => q.kind !== 'board' && Math.hypot(p.x - q.x, p.y - q.y) < q.radius) ?? null;

/** The portal a `door` or `board` interactable fires: the one of its id (content lint checks it exists). */
export const portalFor = (scene: Scene, i: Interactable): Portal | null => (i.kind === 'door' || i.kind === 'board' ? (scene.portals.find((p) => p.id === i.id) ?? null) : null);

/**
 * The interactable kinds with an action (ADR 0033): a ship's furnishings without one (a hatch spot, the farm's plots,
 * bench and rack until the farm loop, a room's fixtures) never answer `interactableNear`, so no prompt shows for them.
 */
export const actionKinds: ReadonlySet<Interactable['kind']> = new Set(['talk', 'read', 'wish', 'door', 'shop', 'board', 'fly', 'incubator']);

/** Moves a walker by (dx, dy) with the prototype's sliding: x first, then y, each only onto walkable ground. Returns the distance moved. */
const slide = (map: WalkMap, w: Walker, dx: number, dy: number): number => {
  const ox = w.x;
  const oy = w.y;
  if (map.canWalk(w.x + dx, w.y)) w.x += dx;
  if (map.canWalk(w.x, w.y + dy)) w.y += dy;
  return Math.hypot(w.x - ox, w.y - oy);
};

/** Starts a route to `to` from where the traveler stands; false (and no route) when none exists. */
export const routeTo = (state: SceneState, to: Point): boolean => {
  const route = findPath(state.map, state.player, to);
  state.player.route = route;
  return route.length > 0;
};

export const cancelRoute = (state: SceneState): void => {
  state.player.route = [];
};

const stepPlayer = (state: SceneState, input: StepInput, dt: number): void => {
  const { walk } = state.scene;
  const p = state.player;
  if (state.held) {
    p.moving = false;
    p.running = false;
    return;
  }
  let mx = 0;
  let my = 0;
  const m = input.move;
  const steering = m !== null && (m.x !== 0 || m.y !== 0);
  if (steering) {
    p.route = [];
    const c = Math.cos(walk.inputTurn);
    const s = Math.sin(walk.inputTurn);
    mx = m.x * c - m.y * s;
    my = m.x * s + m.y * c;
  } else {
    while (p.route.length > 0) {
      const next = p.route[0];
      if (next === undefined || Math.hypot(next.x - p.x, next.y - p.y) >= walk.arrive) break;
      p.route.shift();
    }
    const next = p.route[0];
    if (next !== undefined) {
      mx = next.x - p.x;
      my = next.y - p.y;
    }
  }
  const target = steering ? undefined : p.route[0];
  let length = Math.hypot(mx, my);
  // Route points give a direction, never a speed; a diagonal of two keys is normalised.
  if ((target !== undefined || length > 1) && length > 0) {
    mx /= length;
    my /= length;
    length = 1;
  }
  p.moving = false;
  p.running = false;
  if (length > 0.02) {
    const speed = input.run ? walk.runSpeed : walk.walkSpeed;
    const stride = Math.min(dt * speed, target === undefined ? Infinity : Math.hypot(target.x - p.x, target.y - p.y));
    const ox = p.x;
    const oy = p.y;
    const moved = slide(state.map, p, mx * stride, my * stride);
    p.distance += moved;
    p.moving = moved > 1e-5;
    if (!p.moving && target !== undefined) p.route = [];
    p.facing = facingFor(p.moving ? p.x - ox : mx, p.moving ? p.y - oy : my, p.facing, walk.facingHysteresis);
    p.running = p.moving && input.run;
  }
  p.h = state.map.heightAt(p.x, p.y);
};

const stepResidents = (state: SceneState, dt: number): void => {
  const { walk } = state.scene;
  const p = state.player;
  for (const r of state.residents) {
    r.moving = false;
    if (r.patrol !== null && !state.held && Math.hypot(p.x - r.x, p.y - r.y) > walk.residentPause) {
      r.wait = Math.max(0, r.wait - dt);
      if (r.wait === 0) {
        if (r.route.length === 0) {
          const point = r.patrol[r.patrolIndex];
          if (point !== undefined) r.route = findPath(state.map, r, point);
        }
        while (r.route.length > 0) {
          const next = r.route[0];
          if (next === undefined || Math.hypot(next.x - r.x, next.y - r.y) >= walk.residentArrive) break;
          r.route.shift();
        }
        const next = r.route[0];
        if (next !== undefined) {
          const dx = next.x - r.x;
          const dy = next.y - r.y;
          const length = Math.hypot(dx, dy);
          const stride = Math.min(length, dt * walk.residentSpeed);
          const ox = r.x;
          const oy = r.y;
          const moved = slide(state.map, r, (dx / length) * stride, (dy / length) * stride);
          r.distance += moved;
          r.moving = moved > 1e-5;
          if (r.moving) r.facing = facingFor(r.x - ox, r.y - oy, r.facing, walk.facingHysteresis);
          else {
            r.route = [];
            r.wait = walk.blockedWait;
          }
        } else {
          r.patrolIndex = (r.patrolIndex + 1) % r.patrol.length;
          r.wait = r.pause;
        }
      }
    }
    if (r.patrol !== null) r.h = state.map.heightAt(r.x, r.y);
  }
};

/** What a client draws of the walkers: positions, facings and whether each moves or runs. */
const signature = (state: SceneState): string => {
  let out = '';
  for (const w of [state.player, ...state.residents]) out += `${w.x},${w.y},${w.h},${w.facing},${w.moving ? 1 : 0}${w.running ? 1 : 0};`;
  return out;
};

/** Advances the scene by `dt` seconds: the traveler (by `input`), then the residents; reports a portal entered. */
export const step = (state: SceneState, input: StepInput, dt: number): StepResult => {
  // A step of no time moves nobody (a client's first frame after a pause): it must not read as blocked.
  if (dt <= 0) return { portal: null };
  state.time += dt;
  const before = signature(state);
  stepPlayer(state, input, dt);
  stepResidents(state, dt);
  if (signature(state) !== before) state.revision += 1;
  const portal = portalOf(state.scene, state.player);
  if (portal === null) {
    state.portalArmed = true;
    return { portal: null };
  }
  if (!state.portalArmed) return { portal: null };
  state.portalArmed = false;
  return { portal };
};

/** Whether a patroller is held where it stands because the traveler is near (`walk.residentPause`). */
const paused = (state: SceneState, r: Resident): boolean => state.held || Math.hypot(state.player.x - r.x, state.player.y - r.y) <= state.scene.walk.residentPause;

/**
 * Lets `seconds` pass in which nothing moves (a client that stopped stepping while everyone stood): the scene's
 * clock and the patrollers' waits run down as `step` would count them, but nobody walks.
 */
export const rest = (state: SceneState, seconds: number): void => {
  if (seconds <= 0) return;
  state.time += seconds;
  for (const r of state.residents) if (r.patrol !== null && !paused(state, r)) r.wait = Math.max(0, r.wait - seconds);
};

/**
 * When the walking model next needs a `step` with no input: 0 while the traveler moves or follows a route or a
 * patroller walks; the seconds until the first waiting patroller sets off; null when nothing will change by
 * itself (everyone stands, or every patroller pauses beside the traveler, until the traveler moves).
 */
export const pending = (state: SceneState): number | null => {
  if (state.player.moving || (!state.held && state.player.route.length > 0)) return 0;
  let soonest: number | null = null;
  for (const r of state.residents) {
    if (r.moving) return 0;
    if (r.patrol === null || paused(state, r)) continue;
    if (r.wait <= 0) return 0;
    soonest = soonest === null ? r.wait : Math.min(soonest, r.wait);
  }
  return soonest;
};

/** Where an interactable stands now: a following talk where its resident stands, the others where the record puts them. */
export const interactablePosition = (state: SceneState, i: Interactable): Point => {
  if (i.follows === true) {
    const r = state.residents.find((x) => x.human === i.target);
    if (r !== undefined) return { x: r.x, y: r.y };
  }
  return { x: i.x, y: i.y };
};

/** A following talk whose resident is off the scene (a crew member off deck) cannot be used. */
const absent = (state: SceneState, i: Interactable): boolean => i.follows === true && state.residents.some((r) => r.human === i.target && !r.present);

/** The nearest interactable whose radius the traveler stands in (slice 6b acts on it); a resident's talk follows the resident; one spent this visit is passed over. */
export const interactableNear = (state: SceneState): Interactable | null => {
  let best: Interactable | null = null;
  let distance = Infinity;
  for (const i of state.scene.interactables) {
    if (state.spent.includes(i.id) || !actionKinds.has(i.kind) || absent(state, i)) continue;
    const at = interactablePosition(state, i);
    const d = Math.hypot(state.player.x - at.x, state.player.y - at.y);
    if (d < i.radius && d < distance) {
      best = i;
      distance = d;
    }
  }
  return best;
};

/** What a town's market sells (slice 6b, the shop menu): each `market` entry with a `buy` percent, in the record's order. */
export type Offer = { readonly item: string; readonly price: number };

/**
 * The town's buy prices: an item's `basePrice` × the entry's `buy` percent ÷ 100, rounded down (the one formula;
 * integers, as every price in content). An entry without `buy` is not stocked; an unknown item is skipped.
 */
export const marketOffers = (town: Town, items: Readonly<Record<string, Item>>): Offer[] =>
  town.market.flatMap((entry) => {
    const item = items[entry.item];
    return entry.buy === undefined || item === undefined ? [] : [{ item: entry.item, price: Math.floor((item.basePrice * entry.buy) / 100) }];
  });
